import { useReducer, useRef } from 'react';
import { toast } from 'sonner';
import { ApiError, spacesApi } from '@/lib/api';
import { settlementRequest } from '@/lib/spaces/settlement';
import { initialSettlementDraft, readPendingSettlement, settlementDraftReducer, settlementStorageKey } from '@/lib/spaces/settlementDraft';
import { track } from '@/lib/telemetry';
import type { SpaceLedger } from '@/types/space-settlements';
import { useSpaceWrite } from './queries/useSpaceWrite';

export function useSpaceSettlementDraft(spaceId: string, ledger: SpaceLedger, userId: string, onSuccess: () => void) {
  const { write, mutate } = useSpaceWrite();
  const storageKey = settlementStorageKey(userId, spaceId, ledger.periodKey);
  const [draft, dispatch] = useReducer(settlementDraftReducer, undefined,
    () => initialSettlementDraft(ledger, userId, readPendingSettlement(storageKey)));
  const inFlight = useRef(false);
  const refresh = () => mutate((key) => Array.isArray(key) && key[1] === userId && key[2] === spaceId
    && ['space-ledger', 'space-settlements', 'space-audit'].includes(key[0]));
  const submit = async () => {
    if (inFlight.current || !['select', 'uncertain', 'blocked'].includes(draft.phase)) return;
    const request = draft.request ?? settlementRequest(ledger, draft.selected, crypto.randomUUID());
    if (!request.transfers.length) return;
    // Persist BEFORE sending: closing/reloading must never turn an ambiguous success into a new payment.
    try { sessionStorage.setItem(storageKey, JSON.stringify(request)); }
    catch { dispatch({ type: 'failure', phase: draft.request ? 'uncertain' : 'select', error: 'Permita o armazenamento desta sessão para registrar o acerto com segurança.' }); return; }
    inFlight.current = true;
    dispatch({ type: 'submit', request });
    try {
      await write((token) => spacesApi.registerSettlement(token, spaceId, request));
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0;
      const failure = settlementFailure(status, error);
      if (['conflict', 'select'].includes(failure.phase)) sessionStorage.removeItem(storageKey);
      dispatch(failure);
      toast.error(failure.error);
      return;
    } finally { inFlight.current = false; }
    sessionStorage.removeItem(storageKey);
    track('space.settlement.registered', { transfers: request.transfers.length });
    // Refresh failures must not masquerade as a failed write and invite another registration.
    void refresh().catch(() => undefined);
    toast.success('Acerto registrado. Seu disponível para gastar não mudou.');
    onSuccess();
  };
  return { draft, dispatch, submit };
}

function settlementFailure(status: number, error: unknown): Extract<import('@/lib/spaces/settlementDraft').SettlementAction, { type: 'failure' }> {
  if (status === 409) return { type: 'failure', phase: 'conflict', error: 'Os valores ou as permissões mudaram. Atualize o acerto e confira as transferências antes de confirmar.' };
  if ([401, 403, 404].includes(status)) return { type: 'failure', phase: 'blocked', error: 'Não foi possível autorizar este registro. Confira sua sessão e seu acesso ao espaço.' };
  if (status === 400) return { type: 'failure', phase: 'select', error: error instanceof Error ? error.message : 'Confira as transferências e tente novamente.' };
  return { type: 'failure', phase: 'uncertain', error: 'A confirmação não chegou. O mesmo pedido será reenviado; um registro já confirmado não será duplicado.' };
}
